/**
 * ListFilterBar — stały pasek nad listą. Po task #12 pasek NIE renderuje już
 * kontrolek, które DUBLUJĄ filtry z nagłówków kolumn `ConfigurableTable`:
 *   - dropdowny „Wszystkie <pole>" (TYP/STATUS) — te same wartości filtruje
 *     popup filtra w nagłówku kolumny (zakładka „Wartości") oraz — dla kolumn
 *     z opcjami statycznymi — kontrolka w wierszu filtrów. Zawsze były
 *     duplikatem, więc render usunięty w JEDNYM miejscu (tu).
 *   - pole tekstowe „Szukaj…" — duplikuje per-kolumnowe pola „Szukaj" w wierszu
 *     filtrów; render gatowany `showSearch`. Strony, których kolumny pokrywają
 *     wyszukiwanie tekstem (np. Pracownicy, Kontrahenci), podają
 *     `showSearch={false}`. Strony BEZ pokrycia w kolumnach (dokumenty, moduły
 *     custom, podgląd Kreatora) zostawiają search (domyślnie `true`).
 *
 * Zostaje: przełącznik „Pokaż archiwalne" (nie ma go w kolumnach), odświeżanie
 * listy oraz „Wyczyść filtry" (czyści search + wszystkie filtry nagłówka +
 * archiwum). Zakładki kategorii, przełącznik widoku i przyciski akcji żyją
 * poza tym paskiem (na stronie).
 */

import { useTranslation } from 'react-i18next';
import type { FilterBarStyle, RoundingScale } from '@saas/shared';
import { Archive, RotateCw, X } from 'lucide-react';

import type { ColumnDefinition, ColumnHeaderFilters } from './types';
import { useTableLayout } from './useTableLayout';

/** Skala zaokrąglenia → px (mirror z ListPagePanel — wspólny kontrakt wyglądu). */
const ROUNDING_PX: Record<RoundingScale, number> = {
  none: 0,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  '2xl': 16,
};

/**
 * Buduje inline-styl pola wyszukiwania z `filterBarStyle`. Zwraca pusty obiekt
 * gdy styl nie ustawiony — wtedy zostają klasy motywu.
 */
function fieldStyleFrom(style: FilterBarStyle | undefined): React.CSSProperties {
  if (!style) return {};
  return {
    backgroundColor: style.fieldBackgroundColor,
    color: style.fieldTextColor,
    borderColor: style.fieldBorderColor,
    borderWidth: style.fieldBorderWidth,
    borderStyle: style.fieldBorderWidth != null ? 'solid' : undefined,
    borderRadius: style.fieldRounding != null ? ROUNDING_PX[style.fieldRounding] : undefined,
  };
}

interface ListFilterBarProps<Row> {
  /**
   * Identyfikator tabeli — nieużywany po task #12 (dropdowny distinct usunięte),
   * zachowany dla zgodności sygnatury z wołającymi.
   */
  tableId?: string;
  /**
   * Definicje kolumn — nieużywane po task #12 (dropdowny TYP/STATUS usunięte;
   * filtrowanie tych wymiarów żyje w nagłówkach kolumn). Zachowane dla zgodności
   * sygnatury z wołającymi.
   */
  columns?: ColumnDefinition<Row>[];
  /** Controlled tekst wyszukiwania. */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /**
   * Pokaż input wyszukiwania. Domyślnie true. Strony z pełnym pokryciem
   * wyszukiwania w kolumnach podają `false` (search duplikuje kolumny).
   */
  showSearch?: boolean;
  /** Stan filtrów nagłówka — współdzielony z ConfigurableTable (czyszczony „Wyczyść filtry"). */
  headerFilters: ColumnHeaderFilters;
  onHeaderFiltersChange: (next: ColumnHeaderFilters) => void;
  /** Refetch listy. */
  onRefresh: () => void;
  /**
   * Przełącznik „Pokaż archiwalne". Renderowany TYLKO gdy `onToggleArchived`
   * podane (caller gatuje uprawnieniem `<entity>.view_deleted`).
   */
  showArchived?: boolean;
  onToggleArchived?: (next: boolean) => void;
  /** Prefiks testid (np. 'contractors' → 'contractors-filter-...'). */
  testIdPrefix: string;
  /**
   * Wygląd paska (z Kreatora Listy → „Strona"). Brak propa = pasek sam czyta wygląd z widoku
   * listy `tableId` (to samo zapytanie co tabela, bez dodatkowego pobrania) — dzięki temu
   * ustawienie działa na KAŻDEJ liście, nie tylko tam, gdzie strona przekaże je ręcznie.
   * Brak także w widoku = wygląd motywu. `blockStyle` stylizuje cały pasek, reszta pól — input
   * wyszukiwania.
   */
  filterBarStyle?: FilterBarStyle;
  /**
   * Nieużywane po task #12 (dropdowny distinct usunięte). Zachowane dla
   * zgodności sygnatury z podglądem Kreatora.
   */
  useStaticOptions?: boolean;
}

export function ListFilterBar<Row>({
  search,
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  headerFilters,
  onHeaderFiltersChange,
  onRefresh,
  showArchived,
  onToggleArchived,
  testIdPrefix,
  tableId,
  filterBarStyle: filterBarStyleProp,
}: ListFilterBarProps<Row>): React.ReactElement {
  const { t } = useTranslation();
  const { layout } = useTableLayout(tableId ?? '', {
    enabled: !!tableId && filterBarStyleProp === undefined,
  });
  const filterBarStyle = filterBarStyleProp ?? layout?.layoutConfig?.pageConfig?.filterBarStyle;

  // Wygląd z Kreatora — preset bloku + inline-styl pola wyszukiwania (mirror z ListPagePanel).
  const blockStyle = filterBarStyle?.blockStyle ?? 'default';
  const blockClass =
    blockStyle === 'card'
      ? 'rounded-lg border-2 border-border-secondary bg-bg-elevated shadow-md'
      : blockStyle === 'plain'
        ? ''
        : 'rounded-lg border border-border-secondary bg-bg-elevated';
  const fieldStyle = fieldStyleFrom(filterBarStyle);

  const hasActiveFilters =
    search !== '' || Object.keys(headerFilters).length > 0 || showArchived === true;

  const clearFilters = () => {
    onSearchChange('');
    onHeaderFiltersChange({});
    if (onToggleArchived && showArchived) onToggleArchived(false);
  };

  // Wariant BEZ wyszukiwarki (kartoteki na silniku list) = same przyciski (odśwież,
  // ewentualny „wyczyść filtry"): renderujemy je BEZ obwódkowej karty, żeby wysokością
  // równały się z sąsiednimi kontrolkami paska (Akcje/Kreator/przełącznik). Obwódkowa
  // karta zostaje TYLKO dla wariantu z polem wyszukiwania.
  const containerClass = showSearch
    ? `flex flex-wrap items-center gap-2 px-3 py-2 ${blockClass}`
    : 'flex flex-wrap items-center gap-2';

  return (
    <div className={containerClass} data-testid={`${testIdPrefix}-filter-bar`}>
      {showSearch && (
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder ?? t('common.search', { defaultValue: 'Szukaj…' })}
          className="min-w-[200px] flex-1 rounded-full border border-border-secondary bg-bg-primary px-4 py-1.5 text-sm"
          style={fieldStyle}
          data-testid={`${testIdPrefix}-filter-search`}
        />
      )}

      {onToggleArchived && (
        <button
          type="button"
          onClick={() => onToggleArchived(!showArchived)}
          aria-pressed={showArchived === true}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
            showArchived
              ? 'border-accent-600 bg-accent-50 text-accent-700 dark:bg-accent-900/20 dark:text-accent-300'
              : 'border-border-secondary text-text-secondary hover:bg-bg-secondary'
          }`}
          data-testid={`${testIdPrefix}-filter-archived`}
        >
          <Archive size={14} aria-hidden="true" />
          {showArchived
            ? t('tenant.listFilterBar.showArchivedActive')
            : t('tenant.listFilterBar.showArchived')}
        </button>
      )}

      <button
        type="button"
        onClick={onRefresh}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border-secondary text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary"
        aria-label={t('common.refreshAria')}
        title={t('common.refreshAria')}
        data-testid={`${testIdPrefix}-filter-refresh`}
      >
        <RotateCw size={16} aria-hidden="true" />
      </button>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          data-testid={`${testIdPrefix}-filter-clear`}
        >
          <X size={14} aria-hidden="true" />
          {t('tenant.listFilterBar.clearFilters')}
        </button>
      )}
    </div>
  );
}
